/**
 * Alerts: the bell and its inbox, choosing what you hear, and the rules.
 *
 * The inbox is the signed-in person's own, on any plan. Choosing to hear an
 * alert is Professional; a restaurant's own rules are Enterprise. Choosing
 * nothing, switching a rule off and removing one work on any plan, so a
 * restaurant that moved down can always make the alerts stop.
 *
 * Levels come back as decimal strings, in whatever each site takes: a refund
 * level of "50" is 50 of the site's own currency. Screens show them with
 * `formatMoney` and send back what was typed; nothing here adds them up.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { useApi } from './provider';

export interface Notification {
  id: string;
  /** Which alert it is: `void`, `refund`, `day_not_closed`… */
  kind: string;
  title: string;
  body: string;
  location_id: string | null;
  /** The rule that told you. Null for an alert you chose yourself. */
  rule_id: string | null;
  /** Sent because the people told first had not opened it. */
  escalated: boolean;
  /** When you opened it. Null: not yet. */
  read_at: string | null;
  created_at: string;
}

export interface NotificationChannel {
  /** `in_app` or `email`. */
  key: string;
  label: string;
}

export type ThresholdUnit = 'money' | 'hours' | 'percent';

export interface Alert {
  key: string;
  label: string;
  description: string;
  /** Whether you may hear it: you can already see what it is about. */
  available: boolean;
  /** How you chose to hear it. Empty: you do not. */
  channels: string[];
  /** Only these sites. Null: every site you can see. */
  location_ids: string[] | null;
  /** Null when every one is told and there is no level to set. */
  threshold_unit: ThresholdUnit | null;
  threshold_label: string;
  /** The restaurant's level, decimal string. Null with a unit: every one. */
  threshold: string | null;
  default_threshold: string | null;
}

export interface RuleFact {
  name: string;
  label: string;
  type: 'money' | 'number' | 'text' | 'choice' | 'site';
  /** The tests a rule may put to it: `over`, `under`, `is`, `is_not`, `contains`. */
  ops: string[];
  choices: { value: string; label: string }[];
}

export interface RuleKind {
  key: string;
  label: string;
  description: string;
  facts: RuleFact[];
}

export interface RulePerson {
  id: string;
  name: string;
}

export interface RuleCondition {
  fact: string;
  op: string;
  value: string;
}

export interface AlertRule {
  id: string;
  name: string;
  kind: string;
  conditions: RuleCondition[];
  user_ids: string[];
  channels: string[];
  /** "22:00:00", on the site's own clock. Both or neither. */
  quiet_start: string | null;
  quiet_end: string | null;
  max_per_hour: number | null;
  escalate_after_minutes: number | null;
  escalate_to: string[];
  is_active: boolean;
  /** Times it matched and said nothing because of its hourly cap. */
  held_back: number;
  last_fired_at: string | null;
  created_at: string;
}

export interface AlertRuleInput {
  name: string;
  kind: string;
  conditions: RuleCondition[];
  user_ids: string[];
  channels: string[];
  /** "22:00". Both or neither. */
  quiet_start: string | null;
  quiet_end: string | null;
  max_per_hour: number | null;
  escalate_after_minutes: number | null;
  escalate_to: string[];
  is_active: boolean;
}

const INBOX = ['notifications', 'inbox'] as const;
const ALERTS = ['notifications', 'alerts'] as const;
const RULES = ['notifications', 'rules'] as const;

// --------------------------------------------------------------------------- //
// The inbox
// --------------------------------------------------------------------------- //

/** Your notifications, newest first. */
export function useNotifications(options: { unreadOnly?: boolean } = {}) {
  const api = useApi();
  const unreadOnly = options.unreadOnly ?? false;
  return useQuery({
    queryKey: [...INBOX, 'list', unreadOnly],
    queryFn: ({ signal }) =>
      api.get<Notification[]>(
        `/v1/notifications?limit=100${unreadOnly ? '&unread_only=true' : ''}`,
        signal,
      ),
    refetchInterval: 30_000,
  });
}

/** The number on the bell. Asked every half minute: an alert is raised by the
 * server's clock and other people's actions, never by this screen. */
export function useUnreadNotifications(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...INBOX, 'unread'],
    queryFn: ({ signal }) => api.get<{ count: number }>('/v1/notifications/unread', signal),
    select: (data) => data.count,
    refetchInterval: 30_000,
    enabled,
  });
}

/** Opened: it stops counting, and a rule waiting to escalate it stands down. */
export function useMarkNotificationRead() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Notification, ApiError, { notificationId: string }>({
    mutationFn: ({ notificationId }) =>
      api.post<Notification>(`/v1/notifications/${notificationId}/read`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: INBOX });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<{ marked: number }, ApiError, void>({
    mutationFn: () => api.post<{ marked: number }>('/v1/notifications/read-all'),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: INBOX });
    },
  });
}

// --------------------------------------------------------------------------- //
// Choosing what you hear
// --------------------------------------------------------------------------- //

export function useNotificationChannels() {
  const api = useApi();
  return useQuery({
    queryKey: ['notifications', 'channels'],
    queryFn: ({ signal }) =>
      api.get<NotificationChannel[]>('/v1/notifications/channels', signal),
    staleTime: Infinity,
  });
}

/** Every alert, with what you chose and the restaurant's level for each. */
export function useAlerts() {
  const api = useApi();
  return useQuery({
    queryKey: ALERTS,
    queryFn: ({ signal }) => api.get<Alert[]>('/v1/notifications/alerts', signal),
  });
}

/** 402 below Professional (choosing nothing works on any plan); 403 for an
 * alert about something you cannot see. */
export function useSetMyAlert() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Alert,
    ApiError,
    { kind: string; channels: string[]; locationIds: string[] | null }
  >({
    mutationFn: ({ kind, channels, locationIds }) =>
      api.request<Alert>(`/v1/notifications/alerts/${kind}/mine`, {
        method: 'PUT',
        body: { channels, location_ids: locationIds },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ALERTS });
    },
  });
}

/** The level an alert starts at, for everybody. Null: every one. Managers. */
export function useSetAlertThreshold() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Alert, ApiError, { kind: string; value: string | null }>({
    mutationFn: ({ kind, value }) =>
      api.request<Alert>(`/v1/notifications/alerts/${kind}/threshold`, {
        method: 'PUT',
        body: { value },
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ALERTS });
    },
  });
}

// --------------------------------------------------------------------------- //
// Rules
// --------------------------------------------------------------------------- //

/** What a rule may listen for, and what it may test about each. */
export function useRuleKinds(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...RULES, 'kinds'],
    queryFn: ({ signal }) => api.get<RuleKind[]>('/v1/notifications/rules/kinds', signal),
    staleTime: Infinity,
    enabled,
  });
}

/** Who a rule may tell: everybody with a login that is on. */
export function useRulePeople(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...RULES, 'people'],
    queryFn: ({ signal }) => api.get<RulePerson[]>('/v1/notifications/rules/people', signal),
    enabled,
  });
}

export function useAlertRules(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...RULES, 'list'],
    queryFn: ({ signal }) => api.get<AlertRule[]>('/v1/notifications/rules', signal),
    enabled,
  });
}

function useRuleMutation<TInput, TResult>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<TResult>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<TResult, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [...RULES, 'list'] });
    },
  });
}

/** 402 below Enterprise; 409 with `field` for a part of the rule that will not do. */
export function useCreateAlertRule() {
  return useRuleMutation<AlertRuleInput, AlertRule>((api, input) =>
    api.post<AlertRule>('/v1/notifications/rules', input),
  );
}

/** Rewrite the rule, or only switch it on or off. Off works on any plan. */
export function useUpdateAlertRule() {
  return useRuleMutation<
    { ruleId: string; rule?: AlertRuleInput; isActive?: boolean },
    AlertRule
  >((api, { ruleId, rule, isActive }) =>
    api.request<AlertRule>(`/v1/notifications/rules/${ruleId}`, {
      method: 'PATCH',
      body: {
        ...(rule === undefined ? {} : { rule }),
        ...(isActive === undefined ? {} : { is_active: isActive }),
      },
    }),
  );
}

/** What it already told people stays in their inboxes. Any plan. */
export function useRemoveAlertRule() {
  return useRuleMutation<{ ruleId: string }, void>((api, { ruleId }) =>
    api.request<void>(`/v1/notifications/rules/${ruleId}`, { method: 'DELETE' }),
  );
}
